import MediaNavTabs from '@/components/MediaNavTabs';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import mediaService from '@/services/mediaService';

// Helper function to extract YouTube video ID
const extractYouTubeId = (url) => {
  if (!url) return '';
  
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) return watchMatch[1];
  
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) return shortMatch[1];
  
  const embedMatch = url.match(/embed\/([^?&]+)/);
  if (embedMatch) return embedMatch[1];
  
  return '';
};

// Helper function to format Thai date
const formatThaiDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const thaiMonths = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
  
  const day = date.getDate();
  const month = thaiMonths[date.getMonth()];
  const year = date.getFullYear() + 543;
  
  return `${day} ${month} ${year}`;
};

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Add CSS animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fade-in-up {
        animation: fadeInUp 0.6s ease-out forwards;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Fetch videos from API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await mediaService.getAllMedia({
          type: 'video',
          status: 'published',
          limit: 100
        });
        
        console.log('📹 Videos fetched:', response.data);
        setVideos(response.data || []);
      } catch (err) {
        console.error('❌ Error fetching videos:', err);
        setError('ไม่สามารถโหลดวิดีโอได้ กรุณาลองใหม่อีกครั้ง');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const closeModal = () => {
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative mt-16 sm:mt-20">
        {/* Top Banner */}
        <div className="w-full py-16" style={{
          background: 'linear-gradient(90deg, #BFB4EE 0%, #B3FFD1 100%)'
        }}>
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-center" style={{
              color: '#533192',
              fontFamily: 'Prompt',
              fontSize: 'clamp(1.6rem, 5vw, 3rem)',
              fontStyle: 'normal',
              fontWeight: '700',
              lineHeight: 'normal'
            }}>
              สื่อและข่าวสาร
            </h1>
          </div>
        </div>

        <MediaNavTabs />
        <div className="w-full h-1.5" style={{ background: '#533193' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6 sm:mb-8" style={{ fontFamily: 'Prompt', color: '#333' }}>
          วิดีโอ
        </h2>

        {loading && (
          <div className="h-48 flex items-center justify-center text-gray-500">กำลังโหลด...</div>
        )}

        {error && (
          <div className="text-center py-12 text-red-500">
            <p style={{ fontFamily: 'Prompt' }}>{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && videos.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-gray-600 text-lg" style={{ fontFamily: 'Prompt' }}>ยังไม่มีวิดีโอ</p>
            <p className="text-gray-500 text-sm mt-2" style={{ fontFamily: 'Prompt' }}>กรุณากลับมาตรวจสอบอีกครั้งในภายหลัง</p>
          </div>
        )}

        {/* Videos Grid */}
        {!loading && !error && videos.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 justify-items-center">
            {videos.map((video, index) => {
              const youtubeId = extractYouTubeId(video.video_url);
              const thumbnail = video.cover_image_url || 
                               (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : null);
              
              return (
                <div
                  key={video.id}
                  onClick={() => handleVideoClick(video)}
                  className="relative rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up cursor-pointer"
                  style={{
                    width: '100%',
                    maxWidth: '350px',
                    height: '450px',
                    flexShrink: 0,
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  {/* Background Image */}
                  <div className="absolute inset-0">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={video.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (youtubeId && e.target.src.includes('maxresdefault')) {
                            e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                        <svg className="w-24 h-24 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-purple-900/80"></div>
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                        <div className="w-0 h-0 border-l-[12px] border-l-purple-600 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ml-1"></div>
                      </div>
                    </div>
                  </div>

                  {/* Content Overlay */}
                  <div className="relative h-full flex flex-col justify-end p-6">
                    <div className="space-y-4">
                      {/* Event Tag */}
                      {video.event && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs border border-white/60 text-white px-3 py-1 rounded-full" style={{
                            fontFamily: 'Prompt'
                          }}>
                            {video.event}
                          </span>
                        </div>
                      )}

                      {/* Title */}
                      <h3 className="text-xl font-bold text-white line-clamp-2" style={{
                        fontFamily: 'Prompt'
                      }}>
                        {video.name}
                      </h3>

                      {/* Subtitle */}
                      {video.subtitle && (
                        <p className="text-sm text-white/80 line-clamp-1" style={{
                          fontFamily: 'Prompt'
                        }}>
                          {video.subtitle}
                        </p>
                      )}

                      {/* Date */}
                      {video.date && (
                        <p className="text-sm text-white/70" style={{
                          fontFamily: 'Prompt'
                        }}>
                          {formatThaiDate(video.date)}
                        </p>
                      )}

                      {/* Arrow Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVideoClick(video);
                          }}
                          className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors"
                        >
                          <div className="w-0 h-0 border-l-[8px] border-l-white border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1"></div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="relative bg-white rounded-lg overflow-hidden max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-lg"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Video Player */}
            <div className="relative pt-[56.25%]">
              {selectedVideo.video_url ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={selectedVideo.video_url.includes('youtube.com') 
                    ? selectedVideo.video_url.replace('watch?v=', 'embed/').split('&')[0]
                    : selectedVideo.video_url.includes('youtu.be')
                    ? selectedVideo.video_url.replace('youtu.be/', 'youtube.com/embed/')
                    : selectedVideo.video_url}
                  title={selectedVideo.name}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <p className="text-gray-500" style={{ fontFamily: 'Prompt' }}>ไม่พบวิดีโอ</p>
                </div>
              )}
            </div>

            {/* Video Info */}
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Prompt' }}>
                {selectedVideo.name}
              </h3>
              {selectedVideo.subtitle && (
                <p className="text-gray-700 mb-2" style={{ fontFamily: 'Prompt' }}>
                  {selectedVideo.subtitle}
                </p>
              )}
              <div className="flex items-center gap-4 text-gray-600" style={{ fontFamily: 'Prompt' }}>
                {selectedVideo.event && <span>{selectedVideo.event}</span>}
                {selectedVideo.date && <span>• {formatThaiDate(selectedVideo.date)}</span>}
              </div>
              {selectedVideo.content && (
                <p className="mt-4 text-gray-600" style={{ fontFamily: 'Prompt' }}>
                  {selectedVideo.content}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Videos;